if (!customElements.get('assortment-flavor-slide')) {
  customElements.define(
    'assortment-flavor-slide',
    class AssortmentFlavorSlide extends HTMLElement {
      constructor() {
        super();
        this.track = this.querySelector('.assortment-flavor-slide__track');
        this.mainSlide = this.querySelector('.assortment-flavor-slide__slide--main');
        this.toggle = this.querySelector('.assortment-flavor-toggle');
        this.card = this.closest('.variant-card');
        this.mql = window.matchMedia('(min-width: 769px)');
        this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

        if (!this.track || !this.mainSlide) return;

        this.lockHeightToMainSlide();
        this.setupSwipeHint();
        this.setupKeyboardSwipe();
        this.setupFocusDismiss();
        this.setupToggle();
        this.track.addEventListener('scroll', this.updateBadgeVisibility.bind(this));
      }

      // The badge lives outside this component (a sibling in build-your-own.liquid)
      // so it can overlap the whole tile's corner without being clipped by the
      // slide track. Toggle a class on the card so CSS can hide it once slide 2
      // is scrolled into view.
      updateBadgeVisibility() {
        if (!this.card) return;
        const showingContents = this.track.scrollLeft > this.track.clientWidth / 2;
        this.card.classList.toggle('showing-assortment-contents', showingContents);
      }

      // Slide 1 (image + badge + title) must dictate the row's height, the
      // same way it would on a normal, non-assortment tile. Without this,
      // the flex row stretches both slides to match whichever is taller,
      // and slide 2's contents list can inflate slide 1 well past a normal
      // tile's height.
      lockHeightToMainSlide() {
        const sync = () => {
          const height = this.mainSlide.getBoundingClientRect().height;
          if (height > 0) this.track.style.height = `${height}px`;
        };
        if (window.ResizeObserver) {
          new ResizeObserver(sync).observe(this.mainSlide);
        } else {
          sync();
          window.addEventListener('resize', sync);
        }
      }

      setupSwipeHint() {
        if (this.mql.matches || this.reduceMotion.matches) return;

        // IntersectionObserver only knows about geometric overlap with the
        // viewport - it has no idea the fixed sticky cart bar is painted on
        // top of the bottom of the screen. Shrink the effective viewport by
        // that bar's height so the tile only counts as "in view" once it's
        // actually visible above it, not hidden underneath it.
        const stickyBar = document.querySelector('.sticky-cart-summary');
        const stickyHeight = stickyBar ? stickyBar.offsetHeight : 0;
        this.hintObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            // isIntersecting just means "ratio > 0" regardless of the
            // configured threshold - check the ratio directly to require
            // the tile be fully (not just partially) in view.
            if (entry.intersectionRatio >= 1) {
              // Give the shopper a moment to actually be looking at the tile
              // before nudging it, rather than firing the instant it settles
              // into view.
              this.scheduleSwipeHint(500);
            } else {
              // Scrolled back out before the nudge fired (or before it
              // repeated) - cancel it so it doesn't play off-screen. Stays
              // observed (no disconnect) so the hint can retrigger next time
              // this tile settles back into view.
              clearTimeout(this.hintTimer);
            }
          });
        }, { threshold: 1, rootMargin: `0px 0px -${stickyHeight}px 0px` });
        this.hintObserver.observe(this);

        // A real touch on the track means the shopper found the swipe
        // themselves - dismiss the hint permanently rather than interrupting
        // whatever they're already doing.
        this.track.addEventListener('pointerdown', () => this.dismissSwipeHint(), { passive: true });
      }

      // Re-arms itself after every play, so an unswiped tile keeps nudging
      // every few seconds for as long as it stays in view - not just once.
      scheduleSwipeHint(delay) {
        if (this.hintDismissed) return;
        clearTimeout(this.hintTimer);
        this.hintTimer = setTimeout(() => {
          this.playSwipeHint();
          this.scheduleSwipeHint(3500);
        }, delay);
      }

      dismissSwipeHint() {
        this.hintDismissed = true;
        clearTimeout(this.hintTimer);
        if (this.hintObserver) this.hintObserver.disconnect();
      }

      playSwipeHint() {
        // scroll-snap-type: mandatory snaps back to the nearest snap point
        // (slide 1) after any small scroll, silently undoing a short nudge
        // before it's visible. Suspend snapping for the duration of the peek.
        const previousSnapType = this.track.style.scrollSnapType;
        this.track.style.scrollSnapType = 'none';
        this.track.scrollTo({ left: 32, behavior: 'smooth' });
        setTimeout(() => {
          this.track.scrollTo({ left: 0, behavior: 'smooth' });
          setTimeout(() => {
            this.track.style.scrollSnapType = previousSnapType;
          }, 400);
        }, 500);
      }

      // The swipeable track needs its own keyboard path (WCAG 2.1.1) on
      // mobile - without this, the flavor slide could only ever be reached
      // by touch/mouse drag below the desktop breakpoint. Desktop instead
      // reveals slide 2 via the toggle button (assortment-flavor-slide.css),
      // so the track is taken out of the tab order there to avoid an extra,
      // redundant stop.
      setupKeyboardSwipe() {
        const syncFocusability = () => {
          this.track.tabIndex = this.mql.matches ? -1 : 0;
        };
        syncFocusability();
        this.mql.addEventListener('change', syncFocusability);

        this.track.addEventListener('keydown', (event) => {
          if (this.mql.matches) return;
          if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
          event.preventDefault();
          this.dismissSwipeHint();
          const left = event.key === 'ArrowRight' ? this.track.scrollWidth : 0;
          this.track.scrollTo({ left, behavior: this.reduceMotion.matches ? 'auto' : 'smooth' });
        });
      }

      // Escape is a standard, expected way to close an open disclosure
      // widget - equivalent to clicking the toggle button again.
      setupFocusDismiss() {
        if (!this.card) return;

        this.card.addEventListener('keydown', (event) => {
          if (event.key === 'Escape') this.unpin();
        });
      }

      // The toggle button (WCAG 1.4.13 / 2.1.1) is the sole, explicit,
      // discoverable way to reveal slide 2 on desktop - no more ambient
      // hover/focus triggers. "Pinned" persists independently of hover/focus
      // location until toggled off again or dismissed with Escape, matching
      // standard disclosure-button (aria-expanded) behavior rather than
      // auto-closing on blur.
      setupToggle() {
        if (!this.toggle || !this.card) return;

        this.toggle.addEventListener('click', (event) => {
          event.stopPropagation();
          const opening = this.toggle.getAttribute('aria-expanded') !== 'true';
          this.setPinned(opening);
        });
      }

      unpin() {
        if (!this.toggle) return;
        this.setPinned(false);
      }

      // Shared by the click handler and unpin() so the button's label always
      // matches what it's about to do, not just its aria-expanded state.
      setPinned(pinned) {
        this.toggle.setAttribute('aria-expanded', String(pinned));
        this.card.classList.toggle('assortment-contents-pinned', pinned);
        const label = this.toggle.querySelector('.assortment-flavor-toggle__label');
        if (label) label.textContent = pinned ? 'Hide the flavors' : 'See the flavors';
      }
    }
  );
}
